export type TimesheetStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "withdrawal_requested"
  | "withdrawn";

export interface ManagedFileDetail {
  id: string;
  original_filename: string;
  content_type: string;
  size_bytes: number;
  /** Download link (attachment disposition — saves the file). */
  url: string | null;
  /** Preview link (inline disposition — renders in an iframe/img). */
  inline_url: string | null;
}

export interface SecondaryContractTimesheetBrief {
  id: string;
  kind: string;
  effective_from: string;
  effective_to: string | null;
  hourly_gross_rate: string;
}

export interface Timesheet {
  id: string;
  employee: string;
  employee_name: string;
  year: number;
  month: number;
  period_display: string;
  scan_managed_file: string | null;
  scan_managed_file_detail: ManagedFileDetail | null;
  secondary_contract: string | null;
  secondary_contract_detail: SecondaryContractTimesheetBrief | null;
  note: string;
  status: TimesheetStatus;
  status_display: string;
  reviewed_by: string | null;
  reviewed_by_name: string | null;
  reviewed_at: string | null;
  review_comment: string;
  submitted_by: string | null;
  submitted_by_name: string | null;
  /** Id of the approved card this (re)submission replaces, or null. */
  replaces: string | null;
  withdrawal_requested_at: string | null;
  withdrawal_decided_by: string | null;
  withdrawal_decided_by_name: string | null;
  withdrawal_decided_at: string | null;
  withdrawal_comment: string;
  /** CEO who overturned an already-granted approval, or null.
   *  The original approval stays in reviewed_by / reviewed_at / review_comment. */
  approval_revoked_by: string | null;
  approval_revoked_by_name: string | null;
  approval_revoked_at: string | null;
  approval_revocation_reason: string;
  /** True when the CEO confirmed the reversal despite the month's payouts
   *  having already been actualised for the employee's company. */
  approval_revoked_over_settled_payroll: boolean;
  created_at: string;
  updated_at: string;
}

export const TIMESHEET_STATUS_COLORS: Record<
  TimesheetStatus,
  "yellow" | "green" | "red" | "orange" | "gray"
> = {
  pending: "yellow",
  approved: "green",
  rejected: "red",
  withdrawal_requested: "orange",
  withdrawn: "gray",
};

export const MONTH_NAMES: Record<number, string> = {
  1: "Styczeń", 2: "Luty", 3: "Marzec", 4: "Kwiecień",
  5: "Maj", 6: "Czerwiec", 7: "Lipiec", 8: "Sierpień",
  9: "Wrzesień", 10: "Październik", 11: "Listopad", 12: "Grudzień",
};

/** Per-contract summary of a saved PDF draft (primary or secondary). */
export interface DraftSummary {
  /** null = primary contract; UUID of SecondaryEmploymentContract otherwise. */
  contract_id: string | null;
  /** "uz" itp. dla dodatkowych kontraktów; null gdy primary. */
  contract_kind: string | null;
  default_shift: number | null;
  has_day_states: boolean;
  declared_hours: string | null;
  updated_at: string;
}

/** Append-only audit log entry — one field-level change on a draft. */
export type DraftChangeField =
  | "shift"
  | "ranges"
  | "extra_note"
  | "default_shift"
  | "declared_hours";

export interface DraftHistoryEntry {
  id: string;
  /** 1..31 for per-day fields; null for period-level (default_shift, declared_hours). */
  day: number | null;
  field: DraftChangeField;
  field_display: string;
  old_value: unknown;
  new_value: unknown;
  changed_at: string;
  changed_by_id: string | null;
  changed_by_name: string | null;
}

/** Row in the director's "Brakujące" tab — employee without a submitted primary timesheet. */
export interface TimesheetMissingRow {
  employee_id: string;
  full_name: string;
  username: string;
  branch_id: string | null;
  branch_name: string | null;
  /** True if a rejected timesheet exists (needs resubmission), false if none at all. */
  has_rejected: boolean;
}

/** Row in the director's "Robocze" tab — one employee with their saved drafts. */
export interface DraftOverviewRow {
  employee_id: string;
  employee_name: string;
  branch_name: string | null;
  /** Primary contract type of the employee — "uop" | "uz" | "b2b". */
  primary_contract_kind: string;
  primary_draft: DraftSummary | null;
  secondary_drafts: DraftSummary[];
  /** Status of an already-submitted formal Timesheet for the same period (primary), or null. */
  submitted_primary_status: TimesheetStatus | null;
}

/** Missing-timesheet system lock — see backend apps/timesheets/services/lockout.py. */
export interface TimesheetLockStatus {
  /** True → the app is cut down to the Urlopy + Harmonogram views until a card is filed. */
  locked: boolean;
  /** Is a card for `year`/`month` expected from this user at all (CEO/B2B/contractor: no). */
  required: boolean;
  /** The overdue period. */
  year: number;
  month: number;
  /** e.g. "Sierpień 2026". */
  period_label: string;
  /** ISO date of the 2nd working day of the current month — the card's due date. */
  deadline: string;
  /** The exact due moment: 12:00 (Europe/Warsaw) on `deadline`, as an ISO datetime. */
  deadline_at: string;
}
